import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Card } from '@mui/material';
import SubscribingComponent from './subscribing-component';
import styles from './styles';
import { CalcIneligibleContext } from '../../../../context/calneligibleContext';
import { setCalcIneligibleLoader } from '../../../../actions/calcIneligibleActions';
import { CalcARStatus } from '../../../../interfaces/calcARStatus';
import { NON_EXISTING, WEB_SOCKET_DOMAIN } from '../../../../utility/constants';
import { CalcIneligibleState } from '../../../../reducer/calcIneligibleReducer';
import { Client } from '@stomp/stompjs';

export interface CalculationLoaderProps {
  title: string,
  content: string,
  isLoading: boolean,
  link: string,
  show: boolean,
}

export interface IIneligibleSettingForCalculationLoading {
  code: string,
  description: string,
  isLoading: boolean,
}

const CalculationLoader: FC<CalculationLoaderProps> = (props) => {
  const { state, dispatch } = useContext(CalcIneligibleContext);
  const [calcARStatus, setCalcARStatus] = useState<CalcARStatus | null>(null);
  const [ineligibles, setIneligibles] = useState<IIneligibleSettingForCalculationLoading[]>([]);
  const [lastCalculatedIneligibleIndex, setLastCalculatedIneligibleIndex] = useState(NON_EXISTING);
  const [arConnected, setArConnected] = useState<boolean>(false);
  const [invConnected, setInvConnected] = useState<boolean>(false);

  const arClientRef = useRef<Client | null>(null);
  const invClientRef = useRef<Client | null>(null);

  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new WebSocket(`${WEB_SOCKET_DOMAIN}/calc`),
      reconnectDelay: 5000,
      debug: () => { }, // Disable noisy logs; add `console.log` if needed
    });

    client.onConnect = (frame) => {
      setArConnected(true);
    };

    client.onStompError = (frame) => {
      console.error('❌ STOMP error:', frame.headers['message'], frame.body);
    };

    client.onWebSocketError = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    client.activate();
    arClientRef.current = client;

    return () => {
      client.deactivate();
      setArConnected(false);
    };
  }, []);

  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new WebSocket(`${WEB_SOCKET_DOMAIN}/inventory-ws`),
      reconnectDelay: 5000,
      debug: () => { }, // Disable noisy logs; add `console.log` if needed
    });

    client.onConnect = (frame) => {
      setInvConnected(true);
    };

    client.onStompError = (frame) => {
      console.error('❌ STOMP error:', frame.headers['message'], frame.body);
    };

    client.onWebSocketError = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    client.activate();
    invClientRef.current = client;

    return () => {
      client.deactivate();
      setInvConnected(false);
    };
  }, []);

  useEffect(() => { /* to reset status for next recalculation */
    if (state.hasInitiatedSubscription && state.isLoading) { return; }
    setCalcARStatus(null);
    setLastCalculatedIneligibleIndex(NON_EXISTING);
  }, [state.hasInitiatedSubscription, state.isLoading]);

  useEffect(() => {
    const lastCalculatedIneligibleIndex = getLastCalculatedIneligibleIndex(calcARStatus);
    const updatedIneligibles = state.ineligibleSettings
      .filter(ineligible => !ineligible.disabled)
      .sort((a, b) => a.order - b.order)
      .map((ineligible, index) => ({
        code: ineligible.code, description: ineligible.description,
        isLoading: lastCalculatedIneligibleIndex === NON_EXISTING || index > lastCalculatedIneligibleIndex
      }));
    setIneligibles(updatedIneligibles);

    if (!calcARStatus) return;

    const title = `${calcARStatus?.status.toLowerCase() === 'done' ? 'Calculated' : calcARStatus?.status} ${calcARStatus?.message}`;
    const payload: CalcIneligibleState = { ...state, title };
    if (title === 'Calculation Finished') {
      dispatch(setCalcIneligibleLoader({ ...payload, hasInitiatedSubscription: false, isLoading: false }));
      return;
    } else if (title === 'Calculation Failed') {
      dispatch(setCalcIneligibleLoader({ ...payload, hasInitiatedSubscription: false, isLoading: false, failed: true, link: '' }));
      return;
    }

    dispatch(setCalcIneligibleLoader({ ...payload, hasInitiatedSubscription: true, isLoading: true }));
  }, [calcARStatus, state.ineligibleSettings]);

  const getLastCalculatedIneligibleIndex = (calcARStatus: CalcARStatus | null) => {
    const calculatedIneligibleIndex = ineligibles.findIndex(obj => obj.description === calcARStatus?.message && calcARStatus.status === 'done');
    if (calculatedIneligibleIndex === NON_EXISTING) { return lastCalculatedIneligibleIndex; }
    setLastCalculatedIneligibleIndex(calculatedIneligibleIndex);
    return calculatedIneligibleIndex;
  };

  return (
    <Card variant="outlined" sx={styles.container}>
      {
        arConnected && invConnected &&
        <SubscribingComponent
          state={state}
          dispatch={dispatch}
          setCalcARStatus={setCalcARStatus}
          ineligibles={ineligibles}
          stompClient={{
            arClient: arClientRef.current,
            invClient: invClientRef.current
          }}
          {...props}
        />
      }
    </Card>
  )
}

export default CalculationLoader;